import { create } from 'zustand';
import { api, AdminOrder } from '../lib/api';

interface OrderState {
  orders: AdminOrder[];
  total: number;
  isLoading: boolean;
  error: string | null;
  statusFilter: string | null;
  searchQuery: string;

  setStatusFilter: (status: string | null) => void;
  setSearchQuery: (query: string) => void;
  fetchOrders: () => Promise<void>;
  refundOrder: (id: number, payload?: { amount?: number; reason?: string }) => Promise<AdminOrder>;
}

export const useOrderStore = create<OrderState>((set, get) => ({
  orders: [],
  total: 0,
  isLoading: false,
  error: null,
  statusFilter: null,
  searchQuery: '',

  setStatusFilter: (status) => set({ statusFilter: status }),
  setSearchQuery: (query) => set({ searchQuery: query }),

  fetchOrders: async () => {
    const { statusFilter, searchQuery } = get();
    set({ isLoading: true, error: null });
    try {
      const result = await api.orders.list({
        status_filter: statusFilter || undefined,
        search: searchQuery || undefined,
        limit: 500,
      });
      set({
        orders: result?.orders || [],
        total: result?.total || 0,
        isLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch orders';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  refundOrder: async (id, payload) => {
    const updated = await api.orders.refund(id, payload);
    set((state) => ({
      orders: state.orders.map((o) => (o.id === id ? { ...o, ...updated } : o)),
    }));
    return updated;
  },
}));

export function useOrders() {
  const store = useOrderStore();
  return {
    orders: store.orders,
    total: store.total,
    isLoading: store.isLoading,
    error: store.error,
    statusFilter: store.statusFilter,
    searchQuery: store.searchQuery,
    setStatusFilter: store.setStatusFilter,
    setSearchQuery: store.setSearchQuery,
    fetchOrders: store.fetchOrders,
    refundOrder: store.refundOrder,
  };
}
